import { FC } from 'react';

import { IRoutingConfigRuleParam } from '@entities/routing/config-version';
import { Chip, Stack, Typography } from '@mui/material';

interface IProps {
  params: IRoutingConfigRuleParam[];
}

// Renders a rule's conditions or selections list: multi-select values as chips
// ("Any" when empty), comparators as an operator + value, tag comparators with
// their tag chips ("Any tag" when empty).
const RuleParams: FC<IProps> = ({ params }) => {
  if (!params?.length) {
    return (
      <Typography variant='caption' color='text.secondary'>
        —
      </Typography>
    );
  }

  return (
    <Stack alignItems='flex-start' spacing={1}>
      {params.map(param => {
        const label = formatParamName(param.name);
        // rawValue is raw API data — a missing value degrades to the dash branch
        const rawValue = param.rawValue ?? {};

        if (Array.isArray(rawValue)) {
          return (
            <Stack alignItems='flex-start' key={param.name} spacing={0.5}>
              <Typography variant='caption' color='text.secondary'>
                {label}
              </Typography>
              {rawValue.length ? (
                <Stack direction='row' spacing={0.5} flexWrap='wrap'>
                  {rawValue.map(item => (
                    <Chip sx={{ margin: '2px !important' }} key={item} size='small' label={item} />
                  ))}
                </Stack>
              ) : (
                <Typography variant='body2' fontWeight={600} color='text.primary'>
                  Any
                </Typography>
              )}
            </Stack>
          );
        }

        const { tags, ...comparator } = rawValue;
        const operatorEntry = Object.entries(comparator)[0];
        const hasTags = param.type === 'tag-digit-compare' || Array.isArray(tags);

        return (
          <Stack key={param.name} spacing={0.5} alignItems='flex-start'>
            <Typography variant='caption' color='text.secondary'>
              {label}
            </Typography>
            <Typography variant='body2' fontWeight={600} color='text.primary'>
              {operatorEntry ? `${formatOperator(operatorEntry[0])} ${operatorEntry[1]}` : '—'}
            </Typography>
            {hasTags &&
              (tags?.length ? (
                <Stack direction='row' spacing={0.5} flexWrap='wrap'>
                  {tags.map(tag => (
                    <Chip sx={{ margin: '2px !important' }} key={tag} size='small' label={tag} />
                  ))}
                </Stack>
              ) : (
                <Typography variant='caption' color='text.secondary'>
                  Any tag
                </Typography>
              ))}
          </Stack>
        );
      })}
    </Stack>
  );
};

export default RuleParams;

const formatParamName = (name: string) =>
  name.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

const formatOperator = (op: string) => {
  switch (op) {
    case 'eq':
      return '=';
    case 'gt':
      return '>';
    case 'gte':
      return '≥';
    case 'lt':
      return '<';
    case 'lte':
      return '≤';
    default:
      return op;
  }
};
